import _ from 'lodash'
import type {
	BaseDataItem,
	InternalRefDataItem,
	Component,
	DataMapName,
	ViewMode,
	WidgetRefDataItem,
	QueryProperty,
	ConnectionList,
	Data,
} from '@wix/thunderbolt-becky-types'
import { pointers, Language } from '@wix/thunderbolt-becky-types'
import type { ByRefModelFunctionLibraries, ByRefModelInputs } from 'thunderbolt-ds-carmi-root'
import { getByRefsis } from './byRefsis/byRefsis'
import {
	DATA_MAP_NAMES,
	INFLATED_MAP,
	INFLATED_MAP_TO_DATA_MAP_NAME,
	RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	STRUCTURE_ASPECT,
	RESOLVED_REF_COMP,
	HAS_SITE_OVERRIDE,
	COMP_ID_TO_WIDGET_TEMPLATE_KEY,
	COMPONENTS_WITH_SLOTS_ASPECT,
} from './byRefsis/constants'
import { createRemoteRefsFetcher } from './resolveRemoteRefs'
import type { ByRefsis, ByRefsisOps, RawModel } from './types'
import { getMultipleRefCompsIdFromInflatedId, getRefCompIdFromInflatedId } from './utils'
import { updateWidgetTemplate } from './resolveCommonAspects.site-assets'
import {
	isSharedPart,
	isRefComponent,
	resolveSkin,
	isRefCompConsumed,
	isInflatedId,
	isConnectionItem,
	hasRemoteOverride,
	hasSiteOverride,
} from './byRefsis/byRefUtils'
import { ILogger, LOADING_PHASES } from '@wix/thunderbolt-symbols'

const {
	data: {
		DATA_MAPS: { CONNECTIONS, DATA },
	},
} = pointers

const viewModes: Array<ViewMode> = ['desktop', 'mobile']

const removeHash = (value?: string) => (typeof value === 'string' ? value.replace('#', '') : value)
const fixStructure = (comp: Component): Component => ({
	...comp,
	skin: resolveSkin(_.get(comp, 'skin')),
	dataQuery: removeHash(_.get(comp, 'dataQuery')),
	propertyQuery: removeHash(_.get(comp, 'propertyQuery')),
	patternsQuery: removeHash(_.get(comp, 'patternsQuery')),
	connectionQuery: removeHash(_.get(comp, 'connectionQuery')),
	behaviorQuery: removeHash(_.get(comp, 'behaviorQuery')),
	designQuery: removeHash(_.get(comp, 'designQuery')),
	layoutQuery: removeHash(_.get(comp, 'layoutQuery')),
	breakpointsQuery: removeHash(_.get(comp, 'breakpointsQuery')),
	slotsQuery: removeHash(_.get(comp, 'slotsQuery')),
	styleId: _.get(comp, 'styleId') || _.get(comp, 'id'),
	components: isRefComponent(comp) ? [] : comp.components,
})

export const initByRefsis = (
	{ beckyModel, payload, schemas }: ByRefModelInputs,
	{ byRefFunctionLib, beckyModelFunctionLibrary }: ByRefModelFunctionLibraries,
	logger: ILogger
): ByRefsis => {
	const byRefModel = {
		resolvedRefStructure: {},
		internallyReferredComps: {},
		hiddenCompsGroupedByRefComponent: {},
		displayedConnectionOverrides: {},
		data: {},
	}

	const { fetchUrl } = beckyModelFunctionLibrary

	const byRefModelInput = { ...beckyModel, payload }
	const { experiments, viewMode, originalLanguage, language, publicTopology } = byRefModelInput

	const listeners: Array<VoidFunction> = []
	let clientSpecMap: any = null
	let currentViewMode: ViewMode = viewMode
	let currentLanguage: Language = language

	const _payload = {
		desktop: { ...payload, viewMode: 'desktop' },
		mobile: { ...payload, viewMode: 'mobile' },
	}

	const _createRemoteRefsFetcher = (csm: any, _viewMode: ViewMode) =>
		createRemoteRefsFetcher(
			fetchUrl.bind(beckyModelFunctionLibrary),
			csm,
			publicTopology,
			// @ts-ignore
			_payload[_viewMode],
			true
		)

	const unresolvedRefComps = new Set<string>()
	let isByRefResolvingDone: boolean = true
	const updateByRefResolvingDone = (_isByRefResolvingDone: boolean) => {
		if (isByRefResolvingDone === _isByRefResolvingDone) {
			return
		}

		if (!_isByRefResolvingDone) {
			isByRefResolvingDone = false
			return
		}

		const allRefComponentsResolved = unresolvedRefComps.size === 0
		if (allRefComponentsResolved) {
			isByRefResolvingDone = true
		}
	}

	const updateDsStructure = (id: string, value?: any) => {
		if (value) {
			if (isRefComponent(value)) {
				const _isRefCompConsumed = isRefCompConsumed(value)
				if (_isRefCompConsumed) {
					byRefFunctionLib.updateDsStructure.call(byRefsis, id, value)

					unresolvedRefComps.delete(id)
					updateByRefResolvingDone(true)
				} else {
					unresolvedRefComps.add(id)
					updateByRefResolvingDone(false)

					byRefFunctionLib.updateDsStructure.call(byRefsis, id, value)
				}
			} else {
				byRefFunctionLib.updateDsStructure.call(byRefsis, id, value)
			}
		} else {
			byRefFunctionLib.deleteDsStructure.call(byRefsis, id)
			_.unset(byRefModel, ['displayedConnectionOverrides', id])
		}
		_.set(byRefModel, ['resolvedRefStructure', id], value)
	}

	const updateDisplayedConnectionOverrides = (aspect: string, id: string, value?: any) => {
		// isConnectionItem - This is needed for WixCodeConnectionItems which are not part of the consumption flow
		if (aspect === CONNECTIONS && (!_.isEmpty(value?.items) || isConnectionItem(id))) {
			const context = getMultipleRefCompsIdFromInflatedId(id)
			const connectionsPathById = ['displayedConnectionOverrides', getRefCompIdFromInflatedId(id), context]
			_.update(byRefModel, connectionsPathById, (existing = []) => {
				const connectionList = existing.find((list: ConnectionList) => list.id === id)
				if (connectionList) {
					connectionList.items = value.items
				} else {
					existing.push(value)
				}
				return existing
			})
		}
	}

	const updateDsData = (aspect: DataMapName, id: string, value?: any) => {
		if (value) {
			updateDisplayedConnectionOverrides(aspect, id, value)
			byRefFunctionLib.updateDsData.call(byRefsis, aspect, id, value)
		} else {
			byRefFunctionLib.deleteDsData.call(byRefsis, aspect, id)
		}
	}

	const onInvalidation = (path: Array<string>, value: any) => {
		const [, aspect, id] = path
		const context = getMultipleRefCompsIdFromInflatedId(id)

		if (aspect === STRUCTURE_ASPECT || aspect === RESOLVED_REF_COMP || aspect === COMPONENTS_WITH_SLOTS_ASPECT) {
			updateDsStructure(id, value)
		} else if (aspect === RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT) {
			const hiddenCompsByRefPath = ['hiddenCompsGroupedByRefComponent', context, id]
			if (value?.isGhostComp) {
				_.set(byRefModel, hiddenCompsByRefPath, value.inflatedStructure)
			} else if (_.has(byRefModel, hiddenCompsByRefPath)) {
				_.unset(byRefModel, hiddenCompsByRefPath)
				const parentRefCompPath = ['hiddenCompsGroupedByRefComponent', context]
				if (_.isEmpty(_.get(byRefModel, parentRefCompPath))) {
					_.unset(byRefModel, parentRefCompPath)
				}
			}
		} else if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
			updateDsData(aspect as DataMapName, id, value)

			const pageData = _.pick(value, ['pageUriSEO', 'parentPageId'])
			if (aspect === DATA && !_.isEmpty(pageData)) {
				const lang = currentLanguage === originalLanguage ? 'default' : currentLanguage
				byRefFunctionLib.updateSeoTranslationData.call(byRefsis, lang, id, pageData)
			}
		}

		// Unlike the other cases, each value in all inflatedMap aspects have various inflated dataItems (refArrays, variantRelations and their values).
		// That's why we need to copy ALL properties of the value into the corresponding dataMap
		else if (aspect.endsWith(INFLATED_MAP)) {
			const dataMap = INFLATED_MAP_TO_DATA_MAP_NAME[aspect]
			if (!value) {
				updateDsData(dataMap, id)
			}

			_.forEach(value, (item, key) => {
				updateDsData(dataMap, key, item)
				_.set(byRefModel, ['data', dataMap, key], item)
			})
		}
	}

	const rawModel: RawModel = {}
	const byRefsis = getByRefsis({ onInvalidation, rawModel, schemas })

	byRefsis.transaction(({ updateEnvironment }) => {
		updateEnvironment({
			experiments,
			environment: { viewMode, keepGhostComponents: true, originalLanguage, language },
		})
	})

	// @ts-expect-error
	byRefsis.$addListener = (callback) => listeners.push(callback)

	const internalPagesLoaded: Record<string, true> = {}
	const originalTransaction = byRefsis.transaction
	byRefsis.transaction = (callback) => {
		logger.interactionStarted(LOADING_PHASES.BY_REFSIS_TRANSACTION)
		const isByRefResolvingDoneBeforeTransaction = isByRefResolvingDone
		originalTransaction((ops) => {
			const _invalidateWidget = (value?: Component | BaseDataItem) => {
				const pageId = _.get(value, ['metaData', 'pageId'])
				if (pageId) {
					internalPagesLoaded[pageId] = true
					ops.invalidateWidget(pageId)
				}
			}

			callback({
				...ops,
				updateEnvironment: (env) => {
					currentViewMode = (env.environment?.viewMode as ViewMode) || currentViewMode
					currentLanguage = (env.environment?.language as Language) || currentLanguage
					ops.updateEnvironment(env)
				},
				updateStructure: (id, value) => {
					const fixedStructure = value ? fixStructure(value) : value

					// if ref comp value didn't change, do not update it (it will trigger re-consumption)
					if (fixedStructure && isRefComponent(fixedStructure)) {
						const prevValue = _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, id])
						if (prevValue && _.isEqual(prevValue, fixedStructure)) {
							return
						}
					}
					updateDsStructure(id, fixedStructure)
					ops.updateStructure(id, fixedStructure)
					_invalidateWidget(value)
				},
				updateWidgetStructure: (widgetTemplateKey, id, value) => {
					const fixedStructure = value ? fixStructure(value) : value
					_.set(rawModel, [COMP_ID_TO_WIDGET_TEMPLATE_KEY, id], widgetTemplateKey)
					ops.updateWidgetStructure(widgetTemplateKey, id, fixedStructure)
				},
				updateData: (aspect, id, value) => {
					if (value?.type === 'WidgetRef') {
						const remoteRefFetcher = _createRemoteRefsFetcher(clientSpecMap, currentViewMode)
						remoteRefFetcher({ [value.id]: value }).then((widgetTemplatesStore) => {
							const currentViewModeTemplates = widgetTemplatesStore[currentViewMode]
							if (!_.isEmpty(currentViewModeTemplates)) {
								byRefsis.transaction(
									({
										updateWidgetData,
										updateWidgetStructure,
										updateWidgetRootCompId,
										updateWidgetTranslation,
										invalidateWidget,
									}) => {
										for (const templateKey in currentViewModeTemplates) {
											updateWidgetTemplate(
												currentViewModeTemplates[templateKey],
												templateKey,
												updateWidgetRootCompId,
												updateWidgetTranslation,
												updateWidgetStructure,
												updateWidgetData,
												updateDsData
											)

											invalidateWidget(templateKey)
										}
									}
								)
							}
						})
					} else if (
						value?.type === 'InternalRef' &&
						!isSharedPart((value as unknown) as InternalRefDataItem)
					) {
						const pageId = _.get(value, 'pageId')
						if (!internalPagesLoaded[pageId]) {
							internalPagesLoaded[pageId] = true
							byRefFunctionLib.loadPages.call(byRefsis, [pageId])
						}
					}

					updateDsData(aspect, id, value)
					_.set(byRefModel, ['data', aspect, id], value)

					ops.updateData(aspect, id, value)
					// When we get an update to an inflated data item, the query might not exist on the structure,
					// As we don't inflate any query that there is no data for it in the beginning
					if (isInflatedId(id)) {
						handleUpdateInflatedId(id, aspect, ops.invalidateResolvedStructure, value)
					}
					_invalidateWidget(value)
				},
				updateTranslation: (lang, id, value) => {
					ops.updateTranslation(lang, id, value)
					_invalidateWidget(value)
				},
			})
		})

		logger.interactionEnded(LOADING_PHASES.BY_REFSIS_TRANSACTION)

		if (isByRefResolvingDoneBeforeTransaction !== isByRefResolvingDone) {
			byRefFunctionLib.updateByRefResolvingDone.call(byRefsis, isByRefResolvingDone)
		}
		listeners.forEach((listener) => listener())
	}

	const handleUpdateInflatedId = (
		inflatedDataItemId: string,
		dataMapName: keyof Data,
		invalidateResolvedStructure: ByRefsisOps['invalidateResolvedStructure'],
		value: BaseDataItem | undefined
	) => {
		const queryName = pointers.MAP_NAME_TO_QUERY[dataMapName] as QueryProperty
		const inflatedCompId = inflatedDataItemId.split(`-${queryName}`)[0]
		const currentStructure = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedCompId])

		// We need to update the byrefsis structure node to be aware of the new query
		if (currentStructure && !currentStructure?.[queryName as QueryProperty]) {
			const structureWithNewQuery = { ...currentStructure, [queryName]: inflatedDataItemId }
			_.set(rawModel, [HAS_SITE_OVERRIDE, dataMapName, inflatedCompId], true)
			// We update resolved structure to trigger invalidation in compStructure node
			invalidateResolvedStructure(inflatedCompId, structureWithNewQuery)
		} else if (
			currentStructure &&
			!value &&
			hasSiteOverride(rawModel, dataMapName, inflatedCompId) &&
			currentStructure?.[queryName]
		) {
			// Site override is being removed at this point
			_.set(rawModel, [HAS_SITE_OVERRIDE, dataMapName, inflatedCompId], false)

			// We remove the query from the structure in case it has no other overrides
			// and the template does not contain the query
			if (!hasRemoteOverride(rawModel, queryName, inflatedCompId)) {
				const templateComp = _.get(rawModel, [
					_.get(rawModel, [COMP_ID_TO_WIDGET_TEMPLATE_KEY, currentStructure.templateId]), // templateKey
					STRUCTURE_ASPECT,
					currentStructure.templateId,
				])
				if (!templateComp || templateComp[queryName]) {
					return
				}
				const structureWithNewQuery = { ...currentStructure, [queryName]: undefined }
				// We update resolved structure to trigger invlaidation in compStructure node
				invalidateResolvedStructure(inflatedCompId, structureWithNewQuery)
			}
		}
	}

	// @ts-expect-error
	byRefsis.updateClientSpecMap = (newClientSpecMap) => {
		if (clientSpecMap) {
			const updatedTemplates = _(newClientSpecMap)
				.filter(({ applicationId }) => clientSpecMap[applicationId]) // app already existed
				.pickBy(({ version, applicationId }) => version !== clientSpecMap[applicationId].version) // app version was updated
				.flatMap(({ appDefinitionId, widgets }) =>
					_.map(widgets, (__, widgetId) => ({ appDefinitionId, widgetId, type: 'WidgetRef' }))
				)
				.value() as Array<WidgetRefDataItem>

			if (updatedTemplates.length > 0) {
				const remoteRefFetcher = {
					desktop: _createRemoteRefsFetcher(newClientSpecMap, 'desktop'),
					mobile: _createRemoteRefsFetcher(newClientSpecMap, 'mobile'),
				}

				byRefsis.transaction(
					({
						updateWidgetData,
						updateWidgetStructure,
						updateWidgetRootCompId,
						updateWidgetTranslation,
						invalidateWidget,
					}) => {
						updatedTemplates.forEach((widgetRefDataItem) => {
							viewModes.forEach((_viewMode) => {
								remoteRefFetcher[viewMode]({ '': widgetRefDataItem }).then((widgetTemplatesStore) => {
									for (const _templateKey in widgetTemplatesStore[viewMode]) {
										updateWidgetTemplate(
											widgetTemplatesStore[viewMode][_templateKey],
											_templateKey,
											updateWidgetRootCompId,
											updateWidgetTranslation,
											updateWidgetStructure,
											updateWidgetData,
											updateDsData
										)

										invalidateWidget(_templateKey)
									}
								})
							})
						})
					}
				)
			}
		}

		clientSpecMap = newClientSpecMap
		byRefFunctionLib.updateDsClientSpecMap.call(byRefsis, newClientSpecMap)
	}

	byRefFunctionLib.updateByRefResolvingDone.call(byRefsis, isByRefResolvingDone)
	byRefFunctionLib.updateDsByRefModel.call(byRefsis, byRefModel)
	byRefFunctionLib.updateDsViewMode.call(byRefsis, viewMode)

	return byRefsis
}
